/**
 * The getting-started guide: the steps a new company's admin takes, in order,
 * where each is done, and the API's view of what is done. The API ticks a
 * step from the org's data (modules/onboarding): the screens only show it.
 */
import { computed } from 'vue'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { api } from '@/lib/api'
import { errorText } from '@/lib/errors'
import { toast } from '@/ui/toast'
import { can, session } from '@/lib/auth'
import type { IconName } from '@/ui/icons'

export type StepKey = 'team' | 'company' | 'contacts' | 'catalog' | 'campaign' | 'script' | 'firstCall' | 'results'

export interface GuideStep { key: StepKey; done: boolean; byHand: boolean; figures: Record<string, number | boolean> }
export interface Guide { steps: GuideStep[]; next: StepKey | null; completed: boolean; welcomeSeen: boolean; dismissed: boolean }

/** Where each step is done: its main page (with ?new=1 when the page opens its creation), and a second way when there is one. */
export const STEP_LINKS: Record<StepKey, { icon: IconName; to: string; alt?: { to: string; key: string } }> = {
  team: { icon: 'users', to: '/users?new=1' },
  company: { icon: 'building', to: '/settings' },
  contacts: { icon: 'store', to: '/outlets?new=1', alt: { to: '/import-export', key: 'import' } },
  catalog: { icon: 'package', to: '/catalog' },
  campaign: { icon: 'megaphone', to: '/campaigns?new=1' },
  script: { icon: 'edit', to: '/scripts' },
  firstCall: { icon: 'phone', to: '/agent' },
  results: { icon: 'trending-up', to: '/direction', alt: { to: '/supervision', key: 'supervision' } },
}

export const canFollowGuide = (): boolean => Boolean(session.user) && can('onboarding.guide')

export function useGuide() {
  return useQuery({
    queryKey: computed(() => ['onboarding', session.org?.id]),
    queryFn: () => api<Guide>('/onboarding'),
    enabled: computed(canFollowGuide),
    // Steps tick themselves as the admin works: look again now and then.
    refetchInterval: 20_000,
    refetchOnWindowFocus: true,
  })
}

type Action = 'welcome-seen' | 'dismiss' | 'reopen' | { step: StepKey; done: boolean }

export function useGuideAction() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (a: Action) => typeof a === 'string'
      ? api<Guide>(`/onboarding/${a}`, { method: 'POST' })
      : api<Guide>(`/onboarding/steps/${a.step}/done`, { method: a.done ? 'POST' : 'DELETE' }),
    onSuccess: (g) => qc.setQueryData(['onboarding', session.org?.id], g),
    onError: (e) => toast(errorText(e, 'onboarding', 'onboarding.failed'), 'danger'),
  })
}

/** The first-login tips of an account that does not follow the guide. */
export function useIntro() {
  return useQuery({
    queryKey: computed(() => ['onboarding-intro', session.user?.id]),
    queryFn: () => api<{ seen: boolean }>('/onboarding/intro'),
    enabled: computed(() => Boolean(session.user) && !can('onboarding.guide')),
    staleTime: Infinity,
  })
}

/** Which tips an account gets: the workstation's for an agent, the floor's for a supervisor. */
export const introKind = (): 'agent' | 'supervisor' | 'other' =>
  can('monitor.view') ? 'supervisor' : can('agent.work') ? 'agent' : 'other'
